/**
 * AffDice SuperAdmin messages — the Operators list, adding and deleting one, and every section of the operator popup except Brands.
 *
 * Every key starts `ops.` so no other catalogue can collide with it.
 */
import type { Catalogue } from '@affdice/portal-kit';

export const OPERATORS_MESSAGES = {
  'ops.list.title': {
    en: 'Operators',
    es: 'Operadores',
    'pt-BR': 'Operadores',
  },
  'ops.list.add': {
    en: 'Add New Operator',
    es: 'Añadir nuevo operador',
    'pt-BR': 'Adicionar novo operador',
  },
  'ops.list.addTitle': {
    en: 'Add an operator',
    es: 'Añadir un operador',
    'pt-BR': 'Adicionar um operador',
  },
  'ops.list.searchPlaceholder': {
    en: 'Name, status, or admin email',
    es: 'Nombre, estado o correo del administrador',
    'pt-BR': 'Nome, status ou e-mail do administrador',
  },
  'ops.list.panelTitle': {
    en: 'On the platform',
    es: 'En la plataforma',
    'pt-BR': 'Na plataforma',
  },
  'ops.list.none': {
    en: 'No operators yet.',
    es: 'Aún no hay operadores.',
    'pt-BR': 'Ainda não há operadores.',
  },
  'ops.list.legalName': {
    en: 'Legal name',
    es: 'Razón social',
    'pt-BR': 'Razão social',
  },
  'ops.list.admin': {
    en: 'Admin',
    es: 'Administrador',
    'pt-BR': 'Administrador',
  },
  'ops.list.brands': {
    en: 'Brands',
    es: 'Marcas',
    'pt-BR': 'Marcas',
  },
  'ops.list.affiliates': {
    en: 'Affiliates',
    es: 'Afiliados',
    'pt-BR': 'Afiliados',
  },
  'ops.list.joined': {
    en: 'Joined',
    es: 'Alta',
    'pt-BR': 'Cadastro',
  },
  'ops.list.rejected': {
    en: 'Rejected',
    es: 'Rechazado',
    'pt-BR': 'Rejeitado',
  },
  'ops.list.awaiting': {
    en: 'Awaiting approval',
    es: 'Pendiente de aprobación',
    'pt-BR': 'Aguardando aprovação',
  },
  'ops.list.note': {
    en: 'Status and counts here; deeper reads (affiliates, deals, commission) live on their own screen elsewhere in this console, each one logged per access.',
    es: 'Aquí solo estado y totales; las consultas más detalladas (afiliados, acuerdos, comisión) están en su propia pantalla en otra parte de esta consola, y cada acceso queda registrado.',
    'pt-BR': 'Aqui só status e totais; as consultas mais detalhadas (afiliados, acordos, comissão) ficam em sua própria tela em outra parte deste console, e cada acesso é registrado.',
  },
  'ops.add.legalName': {
    en: 'Legal name',
    es: 'Razón social',
    'pt-BR': 'Razão social',
  },
  'ops.add.legalHint': {
    en: 'As it appears on the contract.',
    es: 'Tal como figura en el contrato.',
    'pt-BR': 'Como consta no contrato.',
  },
  'ops.add.tradingName': {
    en: 'Trading name',
    es: 'Nombre comercial',
    'pt-BR': 'Nome fantasia',
  },
  'ops.add.tradingHint': {
    en: 'What affiliates will see.',
    es: 'Lo que verán los afiliados.',
    'pt-BR': 'O que os afiliados verão.',
  },
  'ops.add.choose': {
    en: 'Choose a country…',
    es: 'Elige un país…',
    'pt-BR': 'Escolha um país…',
  },
  'ops.add.baseCurrency': {
    en: 'Base currency',
    es: 'Moneda base',
    'pt-BR': 'Moeda base',
  },
  'ops.add.ownerName': {
    en: 'First owner’s name',
    es: 'Nombre del primer propietario',
    'pt-BR': 'Nome do primeiro proprietário',
  },
  'ops.add.ownerNameHint': {
    en: 'Optional — a contact detail, not verified.',
    es: 'Opcional: un dato de contacto, sin verificar.',
    'pt-BR': 'Opcional: um dado de contato, não verificado.',
  },
  'ops.add.ownerPhone': {
    en: 'First owner’s phone',
    es: 'Teléfono del primer propietario',
    'pt-BR': 'Telefone do primeiro proprietário',
  },
  'ops.add.optional': {
    en: 'Optional.',
    es: 'Opcional.',
    'pt-BR': 'Opcional.',
  },
  'ops.add.ownerEmail': {
    en: 'First owner’s email',
    es: 'Correo del primer propietario',
    'pt-BR': 'E-mail do primeiro proprietário',
  },
  'ops.add.ownerEmailHint': {
    en: 'They will be able to add the rest of their team.',
    es: 'Podrá añadir al resto de su equipo.',
    'pt-BR': 'Poderá adicionar o resto da equipe.',
  },
  'ops.add.tempPassword': {
    en: 'Temporary password',
    es: 'Contraseña temporal',
    'pt-BR': 'Senha temporária',
  },
  'ops.add.tempHint': {
    en: 'They are made to change it.',
    es: 'Se le obliga a cambiarla.',
    'pt-BR': 'Será obrigado a alterá-la.',
  },
  'ops.add.creating': {
    en: 'Creating…',
    es: 'Creando…',
    'pt-BR': 'Criando…',
  },
  'ops.add.create': {
    en: 'Create operator',
    es: 'Crear operador',
    'pt-BR': 'Criar operador',
  },
  'ops.detail.ariaLabel': {
    en: 'Operator sections',
    es: 'Secciones del operador',
    'pt-BR': 'Seções do operador',
  },
  'ops.detail.group': {
    en: 'Operator',
    es: 'Operador',
    'pt-BR': 'Operador',
  },
  'ops.detail.dashboard': {
    en: 'Dashboard',
    es: 'Panel',
    'pt-BR': 'Painel',
  },
  'ops.detail.goLive': {
    en: 'Go-live',
    es: 'Puesta en marcha',
    'pt-BR': 'Entrada em operação',
  },
  'ops.detail.tiers': {
    en: 'Volume tiers',
    es: 'Tramos de volumen',
    'pt-BR': 'Faixas de volume',
  },
  'ops.detail.usage': {
    en: 'Platform usage',
    es: 'Uso de la plataforma',
    'pt-BR': 'Uso da plataforma',
  },
  'ops.detail.invoices': {
    en: 'Platform invoices',
    es: 'Facturas de la plataforma',
    'pt-BR': 'Faturas da plataforma',
  },
  'ops.detail.affiliates': {
    en: 'Affiliates',
    es: 'Afiliados',
    'pt-BR': 'Afiliados',
  },
  'ops.detail.brands': {
    en: 'Brands',
    es: 'Marcas',
    'pt-BR': 'Marcas',
  },
  'ops.detail.periods': {
    en: 'Periods',
    es: 'Períodos',
    'pt-BR': 'Períodos',
  },
  'ops.detail.subAffiliates': {
    en: 'Sub-affiliates',
    es: 'Subafiliados',
    'pt-BR': 'Subafiliados',
  },
  'ops.detail.changes': {
    en: 'Proposed changes',
    es: 'Cambios propuestos',
    'pt-BR': 'Alterações propostas',
  },
  'ops.detail.profile': {
    en: 'Profile',
    es: 'Perfil',
    'pt-BR': 'Perfil',
  },
  'ops.goLive.title': {
    en: 'Go-live gate',
    es: 'Control de puesta en marcha',
    'pt-BR': 'Controle de entrada em operação',
  },
  'ops.goLive.choose': {
    en: 'Choose a country…',
    es: 'Elige un país…',
    'pt-BR': 'Escolha um país…',
  },
  'ops.goLive.condition': {
    en: 'Condition',
    es: 'Condición',
    'pt-BR': 'Condição',
  },
  'ops.goLive.pass': {
    en: 'Pass',
    es: 'Cumple',
    'pt-BR': 'Atendida',
  },
  'ops.goLive.outstanding': {
    en: 'Outstanding',
    es: 'Pendiente',
    'pt-BR': 'Pendente',
  },
  'ops.tiers.title': {
    en: 'Volume tiers',
    es: 'Tramos de volumen',
    'pt-BR': 'Faixas de volume',
  },
  'ops.tiers.noTerm': {
    en: 'No charge term yet. Zero HYBRID terms are seeded on operator create.',
    es: 'Aún no hay condición de cobro. Al crear el operador se generan condiciones HYBRID en cero.',
    'pt-BR': 'Ainda não há condição de cobrança. Condições HYBRID zeradas são criadas ao criar o operador.',
  },
  'ops.tiers.termLine': {
    en: 'Term {id} · {model}. Bands below bill volume once metering has units.',
    es: 'Condición {id} · {model}. Los tramos de abajo facturan el volumen cuando la medición tenga unidades.',
    'pt-BR': 'Condição {id} · {model}. As faixas abaixo faturam o volume quando a medição tiver unidades.',
  },
  'ops.tiers.noBands': {
    en: 'No bands yet.',
    es: 'Aún no hay tramos.',
    'pt-BR': 'Ainda não há faixas.',
  },
  'ops.tiers.from': {
    en: 'From',
    es: 'Desde',
    'pt-BR': 'De',
  },
  'ops.tiers.to': {
    en: 'To',
    es: 'Hasta',
    'pt-BR': 'Até',
  },
  'ops.tiers.rate': {
    en: 'Rate bps',
    es: 'Tasa bps',
    'pt-BR': 'Taxa bps',
  },
  'ops.tiers.flat': {
    en: 'Flat',
    es: 'Fijo',
    'pt-BR': 'Fixo',
  },
  'ops.tiers.fromUnits': {
    en: 'From units',
    es: 'Desde unidades',
    'pt-BR': 'A partir de unidades',
  },
  'ops.tiers.toUnits': {
    en: 'To units (blank = open)',
    es: 'Hasta unidades (vacío = sin límite)',
    'pt-BR': 'Até unidades (vazio = sem limite)',
  },
  'ops.tiers.saveBand': {
    en: 'Save band',
    es: 'Guardar tramo',
    'pt-BR': 'Salvar faixa',
  },
  'ops.tiers.deleteTitle': {
    en: 'Delete the band starting at {from}?',
    es: '¿Eliminar el tramo que empieza en {from}?',
    'pt-BR': 'Excluir a faixa que começa em {from}?',
  },
  'ops.tiers.deleteBody': {
    en: 'Volume above this point falls back to whatever band covers it next, or to the term\'s own rate if none does. Invoices already issued are unaffected — this changes what future ones are calculated from.',
    es: 'El volumen por encima de este punto pasa al siguiente tramo que lo cubra, o a la tasa propia de la condición si no hay ninguno. Las facturas ya emitidas no cambian: esto modifica la base de cálculo de las futuras.',
    'pt-BR': 'O volume acima deste ponto passa para a próxima faixa que o cubra, ou para a taxa própria da condição se não houver nenhuma. As faturas já emitidas não são afetadas: isso muda a base de cálculo das futuras.',
  },
  'ops.usage.title': {
    en: 'Platform usage',
    es: 'Uso de la plataforma',
    'pt-BR': 'Uso da plataforma',
  },
  'ops.usage.liveBrands': {
    en: 'Live brands',
    es: 'Marcas activas',
    'pt-BR': 'Marcas ativas',
  },
  'ops.usage.unitsTerm': {
    en: 'Units',
    es: 'Unidades',
    'pt-BR': 'Unidades',
  },
  'ops.usage.unitsText': {
    en: 'is what the metering job counted for this operator in the period — the volume the Volume-tiers bands are matched against.',
    es: 'es lo que el proceso de medición contó para este operador en el período: el volumen con el que se comparan los tramos de volumen.',
    'pt-BR': 'é o que o processo de medição contou para este operador no período: o volume com o qual as faixas de volume são comparadas.',
  },
  'ops.usage.overageTerm': {
    en: 'Overage',
    es: 'Exceso',
    'pt-BR': 'Excedente',
  },
  'ops.usage.overageText': {
    en: 'is the part of that volume above whatever the operator\'s charge terms include, and so the part that bills on top rather than inside the agreed amount.',
    es: 'es la parte de ese volumen que supera lo que incluyen las condiciones de cobro del operador, y por tanto la parte que se factura aparte del importe acordado.',
    'pt-BR': 'é a parte desse volume acima do que as condições de cobrança do operador incluem e, portanto, a parte faturada à parte, fora do valor acordado.',
  },
  'ops.usage.bothNote': {
    en: 'Both are counted per period and never carried between them.',
    es: 'Ambos se cuentan por período y nunca se trasladan de uno a otro.',
    'pt-BR': 'Ambos são contados por período e nunca são transferidos entre eles.',
  },
  'ops.usage.none': {
    en: 'No stored meters yet. The metering job writes them each hour.',
    es: 'Aún no hay mediciones guardadas. El proceso de medición las escribe cada hora.',
    'pt-BR': 'Ainda não há medições salvas. O processo de medição as grava a cada hora.',
  },
  'ops.usage.metric': {
    en: 'Metric',
    es: 'Métrica',
    'pt-BR': 'Métrica',
  },
  'ops.usage.units': {
    en: 'Units',
    es: 'Unidades',
    'pt-BR': 'Unidades',
  },
  'ops.usage.overage': {
    en: 'Overage',
    es: 'Exceso',
    'pt-BR': 'Excedente',
  },
  'ops.invoices.title': {
    en: 'Platform invoices',
    es: 'Facturas de la plataforma',
    'pt-BR': 'Faturas da plataforma',
  },
  'ops.invoices.none': {
    en: 'No platform invoices yet.',
    es: 'Aún no hay facturas de la plataforma.',
    'pt-BR': 'Ainda não há faturas da plataforma.',
  },
  'ops.invoices.number': {
    en: 'Number',
    es: 'Número',
    'pt-BR': 'Número',
  },
  'ops.invoices.gross': {
    en: 'Gross',
    es: 'Bruto',
    'pt-BR': 'Bruto',
  },
  'ops.invoices.range': {
    en: '{from} to {to}',
    es: '{from} a {to}',
    'pt-BR': '{from} a {to}',
  },
  'ops.invoices.markPaid': {
    en: 'Mark paid',
    es: 'Marcar como pagada',
    'pt-BR': 'Marcar como paga',
  },
  'ops.invoices.markTitle': {
    en: 'Mark invoice {number} paid?',
    es: '¿Marcar la factura {number} como pagada?',
    'pt-BR': 'Marcar a fatura {number} como paga?',
  },
  'ops.invoices.markBefore': {
    en: 'Records a settlement mark against this invoice, reference',
    es: 'Registra una marca de liquidación en esta factura, con referencia',
    'pt-BR': 'Registra uma marca de liquidação nesta fatura, com a referência',
  },
  'ops.invoices.markAfter': {
    en: ', paid today, and sets the invoice itself to Marked paid. If this clears the operator\'s last invoice issued more than 60 days ago that was still unpaid, it also lifts any platform restriction on them.',
    es: ', pagada hoy, y pasa la propia factura a Pagada. Si con esto se salda la última factura del operador emitida hace más de 60 días que seguía sin pagar, también se levanta cualquier restricción de la plataforma sobre él.',
    'pt-BR': ', paga hoje, e passa a própria fatura para Paga. Se isso quitar a última fatura do operador emitida há mais de 60 dias que ainda estava em aberto, também será removida qualquer restrição da plataforma sobre ele.',
  },
  'ops.invoices.invoiceTitle': {
    en: 'Platform invoice {number}',
    es: 'Factura de la plataforma {number}',
    'pt-BR': 'Fatura da plataforma {number}',
  },
  'ops.invoices.invoiceNote': {
    en: 'Net, tax and gross as billed. Everything here is the invoice as the platform issued it; what has been received against it is recorded separately, as a settlement mark.',
    es: 'Neto, impuesto y bruto tal como se facturó. Todo lo que aparece aquí es la factura tal como la emitió la plataforma; lo recibido contra ella se registra aparte, como una marca de liquidación.',
    'pt-BR': 'Líquido, imposto e bruto como faturado. Tudo aqui é a fatura como a plataforma a emitiu; o que foi recebido contra ela é registrado à parte, como uma marca de liquidação.',
  },
  'ops.profile.title': {
    en: 'Profile',
    es: 'Perfil',
    'pt-BR': 'Perfil',
  },
  'ops.profile.suspend': {
    en: 'Suspend operator',
    es: 'Suspender operador',
    'pt-BR': 'Suspender operador',
  },
  'ops.profile.reactivate': {
    en: 'Reactivate operator',
    es: 'Reactivar operador',
    'pt-BR': 'Reativar operador',
  },
  'ops.profile.wentLive': {
    en: 'Went live',
    es: 'Puesta en marcha',
    'pt-BR': 'Entrada em operação',
  },
  'ops.profile.adminEmail': {
    en: 'Admin email',
    es: 'Correo del administrador',
    'pt-BR': 'E-mail do administrador',
  },
  'ops.profile.adminPhone': {
    en: 'Admin phone',
    es: 'Teléfono del administrador',
    'pt-BR': 'Telefone do administrador',
  },
  'ops.profile.suspendTitle': {
    en: 'Suspend this operator?',
    es: '¿Suspender este operador?',
    'pt-BR': 'Suspender este operador?',
  },
  'ops.profile.reactivateTitle': {
    en: 'Reactivate this operator?',
    es: '¿Reactivar este operador?',
    'pt-BR': 'Reativar este operador?',
  },
  'ops.profile.suspendConfirm': {
    en: 'Suspend',
    es: 'Suspender',
    'pt-BR': 'Suspender',
  },
  'ops.profile.reactivateConfirm': {
    en: 'Reactivate',
    es: 'Reactivar',
    'pt-BR': 'Reativar',
  },
  'ops.profile.suspendBody': {
    en: 'Blocks new brands, campaigns and links, and signs its own staff out of signing back in. Already-issued links keep tracking and commission keeps computing — nothing about existing traffic or money changes.',
    es: 'Bloquea nuevas marcas, campañas y enlaces, e impide que su propio personal vuelva a iniciar sesión. Los enlaces ya emitidos siguen registrando y la comisión sigue calculándose: nada cambia en el tráfico ni en el dinero existentes.',
    'pt-BR': 'Bloqueia novas marcas, campanhas e links, e impede que a própria equipe volte a entrar. Os links já emitidos continuam rastreando e a comissão continua sendo calculada: nada muda no tráfego nem no dinheiro existentes.',
  },
  'ops.profile.reactivateBody': {
    en: 'Its own staff can sign in again, and new brands, campaigns and links can be created again.',
    es: 'Su propio personal puede volver a iniciar sesión y se pueden crear de nuevo marcas, campañas y enlaces.',
    'pt-BR': 'A própria equipe pode entrar novamente e novas marcas, campanhas e links podem ser criados de novo.',
  },
  'ops.dash.title': {
    en: 'Analytics',
    es: 'Analítica',
    'pt-BR': 'Análises',
  },
  'ops.dash.titleFor': {
    en: 'Analytics — {operator}',
    es: 'Analítica — {operator}',
    'pt-BR': 'Análises — {operator}',
  },
  'ops.dash.note': {
    en: 'The same headline figures this operator sees on their own dashboard — a real, cross-tenant read logged per access, not only per session.',
    es: 'Las mismas cifras principales que este operador ve en su propio panel: una lectura real entre inquilinos, registrada en cada acceso y no solo por sesión.',
    'pt-BR': 'Os mesmos números principais que este operador vê no próprio painel: uma leitura real entre tenants, registrada a cada acesso e não só por sessão.',
  },
  'ops.dash.view': {
    en: 'View analytics',
    es: 'Ver analítica',
    'pt-BR': 'Ver análises',
  },
  'ops.dash.provisional': {
    en: 'Provisional — this period is not locked yet.',
    es: 'Provisional: este período aún no está bloqueado.',
    'pt-BR': 'Provisório: este período ainda não está bloqueado.',
  },
  'ops.dash.asOf': {
    en: 'As of {date}.',
    es: 'A fecha de {date}.',
    'pt-BR': 'Em {date}.',
  },
  'ops.changes.title': {
    en: 'Proposed changes',
    es: 'Cambios propuestos',
    'pt-BR': 'Alterações propostas',
  },
  'ops.changes.awaiting': {
    en: '{count} awaiting the operator',
    es: '{count} pendientes del operador',
    'pt-BR': '{count} aguardando o operador',
  },
  'ops.changes.show': {
    en: 'Show proposed changes',
    es: 'Mostrar cambios propuestos',
    'pt-BR': 'Mostrar alterações propostas',
  },
  'ops.changes.none': {
    en: 'Support has proposed nothing to this operator.',
    es: 'Soporte no ha propuesto nada a este operador.',
    'pt-BR': 'O suporte não propôs nada a este operador.',
  },
  'ops.changes.what': {
    en: 'What',
    es: 'Qué',
    'pt-BR': 'O quê',
  },
  'ops.changes.why': {
    en: 'Why',
    es: 'Por qué',
    'pt-BR': 'Por quê',
  },
  'ops.changes.decidedBy': {
    en: 'Decided by',
    es: 'Decidido por',
    'pt-BR': 'Decidido por',
  },
  'ops.changes.proposed': {
    en: 'Proposed',
    es: 'Propuesto',
    'pt-BR': 'Proposto',
  },
  'ops.changes.note': {
    en: 'A proposal is a request, not a change. Only the operator can apply one, and a proposal nobody answers lapses after a fortnight rather than waiting indefinitely against data that has moved on.',
    es: 'Una propuesta es una solicitud, no un cambio. Solo el operador puede aplicarla, y una propuesta que nadie responde caduca a las dos semanas en lugar de esperar indefinidamente frente a datos que ya han cambiado.',
    'pt-BR': 'Uma proposta é um pedido, não uma alteração. Só o operador pode aplicá-la, e uma proposta que ninguém responde expira após duas semanas em vez de esperar indefinidamente diante de dados que já mudaram.',
  },
  'ops.refs.title': {
    en: 'Sub-affiliates',
    es: 'Subafiliados',
    'pt-BR': 'Subafiliados',
  },
  'ops.refs.note': {
    en: 'A real, cross-tenant read — logged per access, not only per session.',
    es: 'Una lectura real entre inquilinos, registrada en cada acceso y no solo por sesión.',
    'pt-BR': 'Uma leitura real entre tenants, registrada a cada acesso e não só por sessão.',
  },
  'ops.refs.view': {
    en: 'View sub-affiliates',
    es: 'Ver subafiliados',
    'pt-BR': 'Ver subafiliados',
  },
  'ops.refs.notOn': {
    en: 'This operator has not turned sub-affiliate recruiting on.',
    es: 'Este operador no ha activado el reclutamiento de subafiliados.',
    'pt-BR': 'Este operador não ativou o recrutamento de subafiliados.',
  },
  'ops.refs.nobody': {
    en: 'Nobody has recruited a sub-affiliate on this operator yet.',
    es: 'Nadie ha reclutado aún un subafiliado en este operador.',
    'pt-BR': 'Ninguém recrutou um subafiliado neste operador ainda.',
  },
  'ops.refs.recruiter': {
    en: 'Recruiter',
    es: 'Reclutador',
    'pt-BR': 'Recrutador',
  },
  'ops.refs.sub': {
    en: 'Sub-affiliate',
    es: 'Subafiliado',
    'pt-BR': 'Subafiliado',
  },
  'ops.refs.level': {
    en: 'Level',
    es: 'Nivel',
    'pt-BR': 'Nível',
  },
  'ops.refs.override': {
    en: 'Override',
    es: 'Comisión adicional',
    'pt-BR': 'Comissão adicional',
  },
  'ops.refs.lifetime': {
    en: 'Lifetime',
    es: 'Duración',
    'pt-BR': 'Duração',
  },
  'ops.refs.from': {
    en: 'From',
    es: 'Desde',
    'pt-BR': 'De',
  },
  'ops.refs.until': {
    en: 'Until',
    es: 'Hasta',
    'pt-BR': 'Até',
  },
  'ops.refs.ongoing': {
    en: 'Ongoing',
    es: 'Vigente',
    'pt-BR': 'Em vigor',
  },
  'ops.refs.readOnly': {
    en: 'Read-only. Recruiting, a recruiter\'s own rate and the network itself are this operator\'s and its affiliates\' own to work — the platform only reads what already exists.',
    es: 'Solo lectura. El reclutamiento, la tasa propia de un reclutador y la propia red son responsabilidad de este operador y de sus afiliados: la plataforma solo lee lo que ya existe.',
    'pt-BR': 'Somente leitura. O recrutamento, a taxa própria de um recrutador e a própria rede são de responsabilidade deste operador e de seus afiliados: a plataforma apenas lê o que já existe.',
  },
  'ops.affs.title': {
    en: 'Affiliates',
    es: 'Afiliados',
    'pt-BR': 'Afiliados',
  },
  'ops.affs.titleFor': {
    en: 'Affiliates — {operator}',
    es: 'Afiliados — {operator}',
    'pt-BR': 'Afiliados — {operator}',
  },
  'ops.affs.note': {
    en: 'A real, cross-tenant read — logged per access, not only per session. Shown here rather than by opening their console, because there is no console session to open.',
    es: 'Una lectura real entre inquilinos, registrada en cada acceso y no solo por sesión. Se muestra aquí y no abriendo su consola, porque no hay ninguna sesión de consola que abrir.',
    'pt-BR': 'Uma leitura real entre tenants, registrada a cada acesso e não só por sessão. É mostrada aqui e não abrindo o console deles, porque não há nenhuma sessão de console para abrir.',
  },
  'ops.affs.view': {
    en: 'View affiliates',
    es: 'Ver afiliados',
    'pt-BR': 'Ver afiliados',
  },
  'ops.affs.none': {
    en: 'No affiliates yet.',
    es: 'Aún no hay afiliados.',
    'pt-BR': 'Ainda não há afiliados.',
  },
  'ops.affs.clicks': {
    en: 'Clicks',
    es: 'Clics',
    'pt-BR': 'Cliques',
  },
  'ops.affs.bot': {
    en: 'Bot',
    es: 'Bot',
    'pt-BR': 'Bot',
  },
  'ops.affs.players': {
    en: 'Players',
    es: 'Jugadores',
    'pt-BR': 'Jogadores',
  },
  'ops.affs.commission': {
    en: 'Commission',
    es: 'Comisión',
    'pt-BR': 'Comissão',
  },
  'ops.affs.dormant': {
    en: 'Dormant',
    es: 'Inactivo',
    'pt-BR': 'Dormente',
  },
  'ops.affs.theySee': {
    en: 'They see: {text}',
    es: 'Ven: {text}',
    'pt-BR': 'Veem: {text}',
  },
  'ops.periods.note': {
    en: 'A real, cross-tenant read — logged per access, not only per session.',
    es: 'Una lectura real entre inquilinos, registrada en cada acceso y no solo por sesión.',
    'pt-BR': 'Uma leitura real entre tenants, registrada a cada acesso e não só por sessão.',
  },
  'ops.periods.view': {
    en: 'View periods',
    es: 'Ver períodos',
    'pt-BR': 'Ver períodos',
  },
  'ops.periods.titleFor': {
    en: 'Periods — {operator}',
    es: 'Períodos — {operator}',
    'pt-BR': 'Períodos — {operator}',
  },
  'ops.periods.none': {
    en: 'No periods yet.',
    es: 'Aún no hay períodos.',
    'pt-BR': 'Ainda não há períodos.',
  },
  'ops.periods.lines': {
    en: 'Lines',
    es: 'Líneas',
    'pt-BR': 'Linhas',
  },
  'ops.periods.statements': {
    en: 'Statements',
    es: 'Extractos',
    'pt-BR': 'Extratos',
  },
  'ops.periods.invoices': {
    en: 'Invoices',
    es: 'Facturas',
    'pt-BR': 'Faturas',
  },
  'ops.periods.payouts': {
    en: 'Payouts',
    es: 'Pagos',
    'pt-BR': 'Pagamentos',
  },
  'ops.periods.deleteRow': {
    en: 'Delete {brand} {label}',
    es: 'Eliminar {brand} {label}',
    'pt-BR': 'Excluir {brand} {label}',
  },
  'ops.periods.deleteTitle': {
    en: 'Delete {phrase}',
    es: 'Eliminar {phrase}',
    'pt-BR': 'Excluir {phrase}',
  },
  'ops.periods.removed': {
    en: 'Removed',
    es: 'Eliminado',
    'pt-BR': 'Removido',
  },
  'ops.periods.empty': {
    en: 'Nothing — this period is empty',
    es: 'Nada: este período está vacío',
    'pt-BR': 'Nada: este período está vazio',
  },
  'ops.periods.kept': {
    en: 'Kept, no longer naming this period',
    es: 'Se conserva, sin nombrar ya este período',
    'pt-BR': 'Mantido, sem citar mais este período',
  },
  'ops.periods.corrections': {
    en: 'Corrections booked elsewhere',
    es: 'Correcciones registradas en otro lugar',
    'pt-BR': 'Correções lançadas em outro lugar',
  },
  'ops.periods.unlinked.one': {
    en: '{count} line will keep the figure and lose the link to what it corrected — recomputing does not restore it',
    es: '{count} línea conservará la cifra y perderá el vínculo con lo que corregía: recalcular no lo restaura',
    'pt-BR': '{count} linha manterá o valor e perderá o vínculo com o que corrigia: recalcular não o restaura',
  },
  'ops.periods.unlinked.other': {
    en: '{count} lines will keep the figure and lose the link to what they corrected — recomputing does not restore it',
    es: '{count} líneas conservarán la cifra y perderán el vínculo con lo que corregían: recalcular no lo restaura',
    'pt-BR': '{count} linhas manterão o valor e perderão o vínculo com o que corrigiam: recalcular não o restaura',
  },
  'ops.periods.confirmLabel': {
    en: 'Type the period to confirm',
    es: 'Escribe el período para confirmar',
    'pt-BR': 'Digite o período para confirmar',
  },
  'ops.periods.deleting': {
    en: 'Deleting…',
    es: 'Eliminando…',
    'pt-BR': 'Excluindo…',
  },
  'ops.periods.permanent': {
    en: 'Delete permanently',
    es: 'Eliminar permanentemente',
    'pt-BR': 'Excluir permanentemente',
  },
  'ops.periods.title': {
    en: 'Periods',
    es: 'Períodos',
    'pt-BR': 'Períodos',
  },
  'ops.detail.team': {
    en: 'Team',
    es: 'Equipo',
    'pt-BR': 'Equipe',
  },
  'ops.team.title': {
    en: 'Team',
    es: 'Equipo',
    'pt-BR': 'Equipe',
  },
  'ops.team.titleFor': {
    en: '{operator}\'s team',
    es: 'Equipo de {operator}',
    'pt-BR': 'Equipe de {operator}',
  },
  'ops.team.crossTenant': {
    en: 'Opening this operator\'s team is logged against your name, as every read of an operator\'s data is.',
    es: 'Abrir el equipo de este operador queda registrado a tu nombre, como toda lectura de datos de un operador.',
    'pt-BR': 'Abrir a equipe deste operador fica registrado em seu nome, como toda leitura de dados de um operador.',
  },
  'ops.team.view': {
    en: 'View team',
    es: 'Ver equipo',
    'pt-BR': 'Ver equipe',
  },
  'ops.team.empty': {
    en: 'Nobody is on this team yet.',
    es: 'Todavía no hay nadie en este equipo.',
    'pt-BR': 'Ainda não há ninguém nesta equipe.',
  },
  'ops.team.forbidden': {
    en: 'You do not have the permission to view operators. Ask the super admin to grant it.',
    es: 'No tienes el permiso para ver operadores. Pide al superadministrador que te lo conceda.',
    'pt-BR': 'Você não tem a permissão para ver operadores. Peça ao superadministrador para concedê-la.',
  },
  'ops.team.readOnly': {
    en: 'You can view this team but not change it. Changing it needs the permission to manage an operator’s team or to reset sign-ins.',
    es: 'Puedes ver este equipo, pero no cambiarlo. Para cambiarlo necesitas el permiso para gestionar el equipo de un operador o restablecer accesos.',
    'pt-BR': 'Você pode ver esta equipe, mas não alterá-la. Para alterá-la é preciso a permissão para gerenciar a equipe de um operador ou redefinir acessos.',
  },
  'ops.team.add': {
    en: 'Add member',
    es: 'Añadir miembro',
    'pt-BR': 'Adicionar membro',
  },
  'ops.team.colPerson': {
    en: 'Person',
    es: 'Persona',
    'pt-BR': 'Pessoa',
  },
  'ops.team.colRole': {
    en: 'Role',
    es: 'Rol',
    'pt-BR': 'Função',
  },
  'ops.team.colBrands': {
    en: 'Brands',
    es: 'Marcas',
    'pt-BR': 'Marcas',
  },
  'ops.team.colSignIn': {
    en: 'Sign-in',
    es: 'Acceso',
    'pt-BR': 'Acesso',
  },
  'ops.team.colActions': {
    en: 'Actions',
    es: 'Acciones',
    'pt-BR': 'Ações',
  },
  'ops.team.actionsFor': {
    en: 'Actions for {name}',
    es: 'Acciones para {name}',
    'pt-BR': 'Ações para {name}',
  },
  'ops.team.allBrands': {
    en: 'All brands',
    es: 'Todas las marcas',
    'pt-BR': 'Todas as marcas',
  },
  'ops.team.owner': {
    en: 'Owner',
    es: 'Propietario',
    'pt-BR': 'Proprietário',
  },
  'ops.team.state.active': {
    en: 'Active',
    es: 'Activo',
    'pt-BR': 'Ativo',
  },
  'ops.team.state.locked': {
    en: 'Locked',
    es: 'Bloqueado',
    'pt-BR': 'Bloqueado',
  },
  'ops.team.state.disabled': {
    en: 'Disabled',
    es: 'Desactivado',
    'pt-BR': 'Desativado',
  },
  'ops.team.mfa.on': {
    en: 'Authenticator set up',
    es: 'Autenticador configurado',
    'pt-BR': 'Autenticador configurado',
  },
  'ops.team.mfa.off': {
    en: 'No authenticator yet',
    es: 'Aún sin autenticador',
    'pt-BR': 'Ainda sem autenticador',
  },
  'ops.team.mustChange': {
    en: 'Has not changed the temporary password',
    es: 'No ha cambiado la contraseña temporal',
    'pt-BR': 'Não alterou a senha temporária',
  },
  'ops.team.action.edit': {
    en: 'Edit',
    es: 'Editar',
    'pt-BR': 'Editar',
  },
  'ops.team.action.brands': {
    en: 'Brands',
    es: 'Marcas',
    'pt-BR': 'Marcas',
  },
  'ops.team.action.disable': {
    en: 'Disable',
    es: 'Desactivar',
    'pt-BR': 'Desativar',
  },
  'ops.team.action.enable': {
    en: 'Enable',
    es: 'Activar',
    'pt-BR': 'Ativar',
  },
  'ops.team.action.mfaReset': {
    en: 'Reset authenticator',
    es: 'Restablecer autenticador',
    'pt-BR': 'Redefinir autenticador',
  },
  'ops.team.action.lock': {
    en: 'Lock',
    es: 'Bloquear',
    'pt-BR': 'Bloquear',
  },
  'ops.team.action.unlock': {
    en: 'Unlock',
    es: 'Desbloquear',
    'pt-BR': 'Desbloquear',
  },
  'ops.team.action.delete': {
    en: 'Delete',
    es: 'Eliminar',
    'pt-BR': 'Excluir',
  },
  'ops.team.delete.title': {
    en: 'Delete {name} permanently',
    es: 'Eliminar a {name} definitivamente',
    'pt-BR': 'Excluir {name} definitivamente',
  },
  'ops.team.delete.body': {
    en: 'This removes the person from the operator for good. It cannot be undone. Their sign-in stops working at once and they cannot be restored.',
    es: 'Esto elimina a la persona del operador para siempre. No se puede deshacer. Su acceso deja de funcionar de inmediato y no se puede restaurar.',
    'pt-BR': 'Isto remove a pessoa do operador para sempre. Não pode ser desfeito. O acesso dela deixa de funcionar na hora e não pode ser restaurado.',
  },
  'ops.team.delete.nameKept': {
    en: 'Their name stays on past records such as periods, statements and invoices.',
    es: 'Su nombre se mantiene en los registros anteriores, como periodos, estados de cuenta y facturas.',
    'pt-BR': 'O nome dela permanece nos registros anteriores, como períodos, extratos e faturas.',
  },
  'ops.team.delete.confirm': {
    en: 'Type {email} to confirm',
    es: 'Escriba {email} para confirmar',
    'pt-BR': 'Digite {email} para confirmar',
  },
  'ops.team.delete.mismatch': {
    en: 'That is not the member’s email address.',
    es: 'Esa no es la dirección de correo del miembro.',
    'pt-BR': 'Esse não é o endereço de e-mail do membro.',
  },
  'ops.team.delete.successor': {
    en: 'New owner',
    es: 'Nuevo propietario',
    'pt-BR': 'Novo proprietário',
  },
  'ops.team.delete.successorHelp': {
    en: '{name} is the only owner. Choose an active member to take over as owner.',
    es: '{name} es el único propietario. Elija a un miembro activo que lo reemplace como propietario.',
    'pt-BR': '{name} é o único proprietário. Escolha um membro ativo para assumir como proprietário.',
  },
  'ops.team.delete.successorPick': {
    en: 'Choose a member',
    es: 'Elija un miembro',
    'pt-BR': 'Escolha um membro',
  },
  'ops.team.delete.successorMissing': {
    en: 'Choose who takes over as owner.',
    es: 'Elija quién asume como propietario.',
    'pt-BR': 'Escolha quem assume como proprietário.',
  },
  'ops.team.delete.none': {
    en: 'There is no other active member to take over. Add or enable another member first, then delete this owner.',
    es: 'No hay otro miembro activo que pueda asumir. Añada o active primero a otro miembro y luego elimine a este propietario.',
    'pt-BR': 'Não há outro membro ativo para assumir. Adicione ou ative outro membro primeiro e depois exclua este proprietário.',
  },
  'ops.team.delete.submit': {
    en: 'Delete permanently',
    es: 'Eliminar definitivamente',
    'pt-BR': 'Excluir definitivamente',
  },
  'ops.team.delete.done': {
    en: '{name} was deleted.',
    es: '{name} fue eliminado.',
    'pt-BR': '{name} foi excluído.',
  },
  'ops.team.delete.doneOwner': {
    en: '{name} was deleted. {successor} is now the owner.',
    es: '{name} fue eliminado. {successor} es ahora el propietario.',
    'pt-BR': '{name} foi excluído. {successor} agora é o proprietário.',
  },
  'ops.team.delete.err.self': {
    en: 'You cannot delete your own account here.',
    es: 'No puede eliminar su propia cuenta aquí.',
    'pt-BR': 'Você não pode excluir a sua própria conta aqui.',
  },
  'ops.team.delete.err.successorRequired': {
    en: 'This is the only owner, so choose who takes over first.',
    es: 'Es el único propietario; elija primero quién lo reemplaza.',
    'pt-BR': 'Este é o único proprietário; escolha primeiro quem assume.',
  },
  'ops.team.delete.err.successorInvalid': {
    en: 'That member cannot take over. Choose another active member.',
    es: 'Ese miembro no puede asumir. Elija a otro miembro activo.',
    'pt-BR': 'Esse membro não pode assumir. Escolha outro membro ativo.',
  },
  'ops.team.edit.title': {
    en: 'Edit {name}',
    es: 'Editar a {name}',
    'pt-BR': 'Editar {name}',
  },
  'ops.team.edit.body': {
    en: 'Change their name or their role. The operator is told, with your reason.',
    es: 'Cambia su nombre o su rol. Se avisa al operador, con tu motivo.',
    'pt-BR': 'Altere o nome ou a função. O operador é avisado, com o seu motivo.',
  },
  'ops.team.brands.title': {
    en: 'Brands for {name}',
    es: 'Marcas de {name}',
    'pt-BR': 'Marcas de {name}',
  },
  'ops.team.brands.body': {
    en: 'Tick the brands they may act on. Tick none to let them act on every brand.',
    es: 'Marca las marcas en las que puede actuar. No marques ninguna para que actúe en todas.',
    'pt-BR': 'Marque as marcas em que a pessoa pode atuar. Não marque nenhuma para que atue em todas.',
  },
  'ops.team.disable.title': {
    en: 'Disable {name}',
    es: 'Desactivar a {name}',
    'pt-BR': 'Desativar {name}',
  },
  'ops.team.disable.body': {
    en: 'They can no longer sign in. Nothing they did is removed, and the account can be enabled again.',
    es: 'Ya no podrá iniciar sesión. No se elimina nada de lo que hizo y la cuenta se puede volver a activar.',
    'pt-BR': 'A pessoa não poderá mais entrar. Nada do que fez é removido, e a conta pode ser ativada de novo.',
  },
  'ops.team.enable.title': {
    en: 'Enable {name}',
    es: 'Activar a {name}',
    'pt-BR': 'Ativar {name}',
  },
  'ops.team.enable.body': {
    en: 'They can sign in again with their existing password and authenticator.',
    es: 'Podrá volver a iniciar sesión con su contraseña y autenticador actuales.',
    'pt-BR': 'A pessoa poderá entrar de novo com a senha e o autenticador atuais.',
  },
  'ops.team.mfaReset.title': {
    en: 'Reset the authenticator for {name}',
    es: 'Restablecer el autenticador de {name}',
    'pt-BR': 'Redefinir o autenticador de {name}',
  },
  'ops.team.mfaReset.body': {
    en: 'They are signed out everywhere and set up a new authenticator app at their next sign-in. Their password does not change.',
    es: 'Se cierra su sesión en todas partes y configurará una nueva app de autenticación al volver a entrar. Su contraseña no cambia.',
    'pt-BR': 'A sessão é encerrada em todos os lugares e a pessoa configura um novo app autenticador no próximo acesso. A senha não muda.',
  },
  'ops.team.lock.title': {
    en: 'Lock the login for {name}',
    es: 'Bloquear el acceso de {name}',
    'pt-BR': 'Bloquear o acesso de {name}',
  },
  'ops.team.lock.body': {
    en: 'They are signed out everywhere and cannot sign in until the login is unlocked. Use this when the account may be in the wrong hands.',
    es: 'Se cierra su sesión en todas partes y no podrá entrar hasta que se desbloquee. Úsalo cuando la cuenta pueda estar en malas manos.',
    'pt-BR': 'A sessão é encerrada em todos os lugares e a pessoa não poderá entrar até o desbloqueio. Use quando a conta puder estar em mãos erradas.',
  },
  'ops.team.unlock.title': {
    en: 'Unlock the login for {name}',
    es: 'Desbloquear el acceso de {name}',
    'pt-BR': 'Desbloquear o acesso de {name}',
  },
  'ops.team.unlock.body': {
    en: 'They can sign in again. Any lockout from failed sign-in attempts is cleared too.',
    es: 'Podrá volver a entrar. También se borra cualquier bloqueo por intentos fallidos.',
    'pt-BR': 'A pessoa poderá entrar de novo. Qualquer bloqueio por tentativas falhas também é removido.',
  },
  'ops.team.field.email': {
    en: 'Email',
    es: 'Correo',
    'pt-BR': 'E-mail',
  },
  'ops.team.field.emailInvalid': {
    en: 'Enter a full email address.',
    es: 'Escribe una dirección de correo completa.',
    'pt-BR': 'Digite um endereço de e-mail completo.',
  },
  'ops.team.field.name': {
    en: 'Name',
    es: 'Nombre',
    'pt-BR': 'Nome',
  },
  'ops.team.field.nameHint': {
    en: 'Optional. How the operator knows them.',
    es: 'Opcional. Cómo los conoce el operador.',
    'pt-BR': 'Opcional. Como o operador conhece a pessoa.',
  },
  'ops.team.field.role': {
    en: 'Role',
    es: 'Rol',
    'pt-BR': 'Função',
  },
  'ops.team.field.rolePick': {
    en: 'Choose a role',
    es: 'Elige un rol',
    'pt-BR': 'Escolha uma função',
  },
  'ops.team.field.roleMissing': {
    en: 'Choose a role.',
    es: 'Elige un rol.',
    'pt-BR': 'Escolha uma função.',
  },
  'ops.team.field.ownerRole': {
    en: 'The owner keeps the owner role.',
    es: 'El propietario conserva el rol de propietario.',
    'pt-BR': 'O proprietário mantém a função de proprietário.',
  },
  'ops.team.field.brands': {
    en: 'Limit to brands',
    es: 'Limitar a marcas',
    'pt-BR': 'Limitar a marcas',
  },
  'ops.team.field.brandsHint': {
    en: 'Leave all unticked to let them act on every brand.',
    es: 'Déjalas sin marcar para que actúe en todas las marcas.',
    'pt-BR': 'Deixe todas desmarcadas para que atue em todas as marcas.',
  },
  'ops.team.field.noBrands': {
    en: 'This operator has no brands yet.',
    es: 'Este operador aún no tiene marcas.',
    'pt-BR': 'Este operador ainda não tem marcas.',
  },
  'ops.team.field.reason': {
    en: 'Reason',
    es: 'Motivo',
    'pt-BR': 'Motivo',
  },
  'ops.team.field.reasonHint': {
    en: 'Kept with the change and shown to the operator.',
    es: 'Se guarda con el cambio y se muestra al operador.',
    'pt-BR': 'Fica registrado com a alteração e é mostrado ao operador.',
  },
  'ops.team.field.reasonMissing': {
    en: 'Say why.',
    es: 'Indica el motivo.',
    'pt-BR': 'Informe o motivo.',
  },
  'ops.team.add.title': {
    en: 'Add a member to {operator}',
    es: 'Añadir un miembro a {operator}',
    'pt-BR': 'Adicionar um membro a {operator}',
  },
  'ops.team.add.body': {
    en: 'They are emailed a temporary password that nobody else sees, including you. They change it at their first sign-in.',
    es: 'Se le envía por correo una contraseña temporal que nadie más ve, ni siquiera tú. La cambiará al entrar por primera vez.',
    'pt-BR': 'A pessoa recebe por e-mail uma senha temporária que ninguém mais vê, nem você. Ela a altera no primeiro acesso.',
  },
  'ops.team.add.submit': {
    en: 'Add member',
    es: 'Añadir miembro',
    'pt-BR': 'Adicionar membro',
  },
  'ops.team.add.done': {
    en: '{email} was added and emailed a temporary password.',
    es: 'Se añadió a {email} y se le envió una contraseña temporal.',
    'pt-BR': '{email} foi adicionado e recebeu uma senha temporária por e-mail.',
  },
  'ops.team.confirm': {
    en: 'Confirm',
    es: 'Confirmar',
    'pt-BR': 'Confirmar',
  },
  'ops.team.working': {
    en: 'Working…',
    es: 'Procesando…',
    'pt-BR': 'Processando…',
  },
  'ops.team.nothingChanged': {
    en: 'Nothing has changed yet.',
    es: 'Todavía no ha cambiado nada.',
    'pt-BR': 'Nada foi alterado ainda.',
  },
  'ops.team.done': {
    en: 'Done for {name}. The operator has been told.',
    es: 'Hecho para {name}. Se avisó al operador.',
    'pt-BR': 'Feito para {name}. O operador foi avisado.',
  },
} as const satisfies Catalogue;
